import { hasValue, noValue } from "./general";

export type ValidatorFn<T> = (o: T) => string;
export type MultiValidatorFn<T, TOut> = (o: T) => string | TOut[];

const ZIP_CODE_REGEX = /(^\d{5}$)|(^\d{9}$)|(^\d{5}-\d{4}$)/;
const SSN_REGEX = /(^\d{3})-(\d{2})-(\d{4})/;
const PHONE_NUMBER_REGEX = /\([2-9]\d{2}\) \d{3}-\d{4}(\d+)?/;
const PHONE_NUMBER_LEADING_ONES_REGEX = /\([1-9]\d{2}\) \d{3}-\d{4}(\d+)?/;
const EMAIL_ADDRESS_REGEX =
	/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const required =
	(label = "This field", message = null) =>
	(o: any) => {
		return noValue(o) ? message || `${label} is required` : null;
	};

export const requiredIf =
	(o: any, label = "This field", message = null) =>
	(o2: any) =>
		o ? required(label, message)(o2) : null;

export const requiredIfExists =
	(o: any, label = "This field", message = null) =>
	(o2: any) =>
		hasValue(o) ? required(label, message)(o2) : null;

export const maxLength = (maxValue: number, _label?: string) => (o: string) =>
	!o || !hasValue(maxValue) || o.length <= maxValue ? null : "Max length: " + maxValue;

export const minLength = (minValue: number, _label?: string) => (o: string) =>
	!o || !hasValue(minValue) || o.length >= minValue ? null : "Min length: " + minValue;

export const exactLength = (exact: number, _label?: string) => (o: string) =>
	!o || o.length === exact ? null : "Must be exact length: " + exact;

export const zipCode = (value: string) => testRegex(ZIP_CODE_REGEX, "Invalid Zip Code")(value);

export const ssn = (value: string) => testRegex(SSN_REGEX, "Invalid SSN")(value);

export const phoneNumber = (value: string, allowLeadingOnes = false) =>
	testRegex(allowLeadingOnes ? PHONE_NUMBER_LEADING_ONES_REGEX : PHONE_NUMBER_REGEX, "Invalid Phone Number")(value);

export const emailAddress = (value: string) => testRegex(EMAIL_ADDRESS_REGEX, "Invalid Email Address")(value);

export const testRegex = (regex: RegExp, message: string) => (o: any) =>
	!regex || !o || regex.test(o) ? null : message;

export const max = (maxValue: number, label?: string) => (o: number | string) =>
	!isNumber(o) || !hasValue(maxValue) || parseFloat(o as string) <= maxValue
		? null
		: `${label} must be less than or equal to ${maxValue}`;

export const min = (minValue: number, label?: string) => (o: number | string) =>
	!isNumber(o) || !hasValue(minValue) || parseFloat(o as string) >= minValue
		? null
		: `${label} must be greater than or equal to ${minValue}`;

export const isNumber = (o: number | string) => {
	return hasValue(o) && o !== "";
};

export const mustBeTrue = (label = "This field") => mustBeTrueMsg(`${label} is required`);

export const mustBeTrueMsg = (msg: string) => (o: boolean) => (o ? null : msg);

export const mustBeTrueIf =
	(check: any, label = "This field") =>
	(o: boolean) =>
		check ? mustBeTrue(label)(o) : null;

/** Returns the first truthy message, otherwise the last validator's falsy result (undefined when there are none). */
function firstError<T>(validators: ValidatorFn<T>[], value: T) {
	let result: string;
	for (const validate of validators) {
		result = validate(value);
		if (result) break;
	}
	return result;
}

export const getValidator =
	<TObj>(validators: any, active = true) =>
	(prop: string & keyof TObj) => {
		if (!active) return undefined;
		const propValidators: ValidatorFn<TObj[typeof prop]>[] = validators[prop];
		if (!propValidators || !propValidators.length) return undefined;
		return (v: any) => firstError(propValidators, v);
	};

export const getPropValidator =
	<TProp>(...validators: ValidatorFn<TProp>[]) =>
	(v: TProp) =>
		firstError(validators, v);
